"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import {
    createUser,
    getAuthToken,
    getToken,
    loginUser,
    sendOtp,
    updateUserProfile,
    verifyOtp,
} from "@/lib/auth";
import { useAuth } from "@/context/AuthContext";

const OTP_LENGTH = 5;
const OTP_EXPIRY_SECONDS = 5 * 60;
const OTP_RESEND_SECONDS = 30;

function Spinner() {
    return (
        <svg
            className="h-4 w-4 animate-spin"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
            <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        </svg>
    );
}

function formatCountdown(seconds) {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    const minutes = String(Math.floor(safeSeconds / 60)).padStart(2, "0");
    const remainingSeconds = String(safeSeconds % 60).padStart(2, "0");
    return `${minutes}:${remainingSeconds}`;
}

function normalizeChannels(channels, fallback) {
    const allowed = ["email", "phone"];
    const next = Array.isArray(channels) ? channels.filter((channel) => allowed.includes(channel)) : [];
    if (next.length > 0) {
        return next;
    }

    return [fallback];
}

function mapAuthError(error) {
    const status = error?.status;
    const code = String(error?.code || error?.payload?.code || "").toLowerCase();

    // Check for backend error message structure: { status: { message: "..." } }
    if (error?.payload?.status?.message) {
        return error.payload.status.message;
    }

    if (status === 400 || code.includes("invalid")) {
        return "Data tidak valid. Periksa input dan coba lagi.";
    }

    if (status === 409 || code.includes("conflict")) {
        // 409 means account is fully registered (not just pending verification)
        return "Akun sudah terdaftar. Silakan login.";
    }

    if (status === 410 || code.includes("expired")) {
        return "OTP sudah kedaluwarsa. Minta kode baru.";
    }

    if (status === 429 || code.includes("rate")) {
        const retryAfter = Number(error?.retryAfter || error?.payload?.retryAfter || 0);
        return retryAfter > 0
            ? `Terlalu banyak percobaan. Coba lagi dalam ${retryAfter} detik.`
            : "Terlalu banyak percobaan. Coba lagi nanti.";
    }

    if (error?.message) {
        return error.message;
    }

    return "Terjadi kesalahan. Coba lagi.";
}

function getTimestamp() {
    return new Date().getTime();
}

export default function AuthForm() {
    const { loginData, showToast, authModalMode, openAuthModal } = useAuth();
    const mode = authModalMode;
    const [inputValue, setInputValue] = useState("");
    const [nameValue, setNameValue] = useState("");
    const [loginMethod, setLoginMethod] = useState("phone");
    const [step, setStep] = useState("input");
    const [showValidation, setShowValidation] = useState(false);
    const [otpValues, setOtpValues] = useState(Array(OTP_LENGTH).fill(""));
    const [loadingAction, setLoadingAction] = useState("");
    const [notice, setNotice] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [userId, setUserId] = useState("");
    const [userRecord, setUserRecord] = useState(null);
    const [availableChannels, setAvailableChannels] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState("");
    const [otpExpiresAt, setOtpExpiresAt] = useState(0);
    const [resendAvailableAt, setResendAvailableAt] = useState(0);
    const [clockTick, setClockTick] = useState(() => getTimestamp());
    const otpRefs = useRef([]);

    const isPhone = loginMethod === "phone";
    const isEmail = loginMethod === "email";
    const trimmedInput = inputValue.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = isEmail ? emailPattern.test(trimmedInput) : false;
    const isPhoneValid = isPhone
        ? inputValue.length >= 9 && inputValue.length <= 12 && inputValue.startsWith("8")
        : false;
    const canContinue = isPhone ? isPhoneValid : isEmailValid;
    const validationMessage = isPhone
        ? "Nomor harus diawali 8 dan 9-12 digit."
        : "Email tidak valid.";
    const isBusy = Boolean(loadingAction);
    const otpSecondsLeft = step === "otp" && otpExpiresAt
        ? Math.max(0, Math.ceil((otpExpiresAt - clockTick) / 1000))
        : 0;
    const resendSecondsLeft = resendAvailableAt
        ? Math.max(0, Math.ceil((resendAvailableAt - clockTick) / 1000))
        : 0;

    const normalizePhone = (value) => {
        const digits = value.replace(/[^\d]/g, "");
        if (!digits) return "";
        let normalized = digits;
        if (normalized.startsWith("62")) normalized = normalized.slice(2);
        if (normalized.startsWith("0")) normalized = normalized.slice(1);
        return normalized;
    };

    const handleInputChange = (e) => {
        const next = e.target.value;
        if (isPhone) {
            setInputValue(normalizePhone(next));
            return;
        }

        setInputValue(next);
    };

    const handlePaste = (e) => {
        const pasted = e.clipboardData.getData("text");
        e.preventDefault();

        if (isPhone) {
            setInputValue(normalizePhone(pasted));
            return;
        }

        setInputValue(pasted.trim());
    };

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setClockTick(getTimestamp());
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, []);

    useEffect(() => {
        if (step === "otp") {
            otpRefs.current[0]?.focus();
        }
    }, [step]);

    const contactLabel = isPhone
        ? `+62 ${inputValue || ""}`.trim()
        : inputValue;

    const resetFeedback = () => {
        setNotice("");
        setErrorMessage("");
    };

    const resetFlow = () => {
        setInputValue("");
        setNameValue("");
        setStep("input");
        setShowValidation(false);
        setOtpValues(Array(OTP_LENGTH).fill(""));
        setOtpExpiresAt(0);
        setResendAvailableAt(0);
        setSelectedChannel("");
        setAvailableChannels([]);
        setUserId("");
        setUserRecord(null);
        resetFeedback();
    };

    const handleToggleMethod = () => {
        setLoginMethod(isPhone ? "email" : "phone");
        resetFeedback();
        setInputValue("");
        setStep("input");
        setShowValidation(false);
    };

    const handleToggleMode = () => {
        const nextMode = mode === "login" ? "register" : "login";
        openAuthModal(nextMode);
        // When switching modes, keep the contact method (email/phone) but reset other fields
        setInputValue("");
        setNameValue("");
        setStep("input");
        setShowValidation(false);
        setOtpValues(Array(OTP_LENGTH).fill(""));
        setOtpExpiresAt(0);
        setResendAvailableAt(0);
        setSelectedChannel("");
        setAvailableChannels([]);
        setUserId("");
        setUserRecord(null);
        resetFeedback();
    };

    const resolveContactPayload = () => {
        if (isPhone) {
            return {
                phone: `+62${normalizePhone(trimmedInput)}`,
            };
        }

        return { email: trimmedInput.toLowerCase() };
    };

    const startOtpTimers = (expiresIn = OTP_EXPIRY_SECONDS, cooldown = OTP_RESEND_SECONDS) => {
        const timestamp = getTimestamp();
        setOtpExpiresAt(timestamp + expiresIn * 1000);
        setResendAvailableAt(timestamp + cooldown * 1000);
    };

    const handleLogin = async () => {
        setLoadingAction("login");
        resetFeedback();

        try {
            const payload = resolveContactPayload();
            const response = await loginUser(payload);
            const data = response?.data || response;

            const nextUserId = data?.id || data?.userId;
            if (!nextUserId) throw new Error("User ID tidak ditemukan.");

            setUserId(nextUserId);

            const channels = normalizeChannels(data?.channels_sent || data?.channels, isPhone ? "phone" : "email");
            setAvailableChannels(channels);

            if (channels.length > 1) {
                setStep("channel");
                return;
            }

            setSelectedChannel(channels[0]);
            setStep("otp");
            setOtpValues(Array(OTP_LENGTH).fill(""));
            startOtpTimers(OTP_EXPIRY_SECONDS, OTP_RESEND_SECONDS);
            setNotice("OTP sent");
        } catch (error) {
            setErrorMessage(mapAuthError(error));
        } finally {
            setLoadingAction("");
        }
    };

    const handleRegister = async () => {
        setLoadingAction("register");
        resetFeedback();

        try {
            const payload = {
                name: nameValue.trim(),
                ...resolveContactPayload(),
            };

            const response = await createUser(payload);
            const data = response?.data || response || {};
            const registrationData = data?.user || data;
            const nextUserId = registrationData?.id || data?.id || response?.id || response?.userId;
            const isVerificationPending = data?.verification_pending === true || data?.verificationPending === true;

            const tokenToSave = data?.token || response?.token;
            const tokenExpires = data?.expiresIn || response?.expiresIn;
            if (tokenToSave) {
                loginData(tokenToSave, tokenExpires);
                showToast("Berhasil register!");
                return;
            }

            if (!nextUserId) {
                throw new Error("User ID tidak ditemukan dari respons server.");
            }

            setUserId(nextUserId);
            setUserRecord(registrationData || { ...payload, id: nextUserId });

            const channels = normalizeChannels(
                data?.pending_channels
                || data?.pendingChannels
                || data?.channels_sent
                || data?.channels
                || response?.availableChannels
                || response?.channels,
                isPhone ? "phone" : "email",
            );
            setAvailableChannels(channels);

            if (channels.length > 1) {
                setStep("channel");
                setNotice("Pilih channel verifikasi.");
                return;
            }

            setSelectedChannel(channels[0]);
            setStep("otp");
            setOtpValues(Array(OTP_LENGTH).fill(""));
            startOtpTimers(OTP_EXPIRY_SECONDS, OTP_RESEND_SECONDS);
            setNotice(isVerificationPending ? "OTP baru sudah dikirim." : "OTP sent");
        } catch (error) {
            setErrorMessage(mapAuthError(error));
        } finally {
            setLoadingAction("");
        }
    };

    const handleContinue = async () => {
        if (step === "input") {
            if (!canContinue) {
                setShowValidation(true);
                return;
            }

            if (mode === "login") {
                await handleLogin();
                return;
            }

            setShowValidation(false);
            await handleRegister();
        }
    };

    async function handleSendOtp(targetUserId, targetChannel, isResend = false) {
        if (!targetUserId || !targetChannel) {
            return;
        }

        setLoadingAction(isResend ? "resend-otp" : "send-otp");
        resetFeedback();

        try {
            const response = await sendOtp(targetUserId, targetChannel);
            setSelectedChannel(targetChannel);
            setStep("otp");
            setOtpValues(Array(OTP_LENGTH).fill(""));
            startOtpTimers(response?.expiresIn || OTP_EXPIRY_SECONDS, response?.retryAfter || OTP_RESEND_SECONDS);
            setNotice("OTP sent");
        } catch (error) {
            const message = mapAuthError(error);
            setErrorMessage(message);

            if (error?.retryAfter) {
                setResendAvailableAt(getTimestamp() + Number(error.retryAfter) * 1000);
            }
        } finally {
            setLoadingAction("");
        }
    }

    const handleChannelSelect = async (channelValue) => {
        setSelectedChannel(channelValue);
        await handleSendOtp(userId, channelValue, false);
    };

    const handleOtpChange = (index, value) => {
        const digit = value.replace(/\D/g, "").slice(-1);
        setOtpValues((prev) => {
            const next = [...prev];
            next[index] = digit;
            return next;
        });
        if (digit && otpRefs.current[index + 1]) {
            otpRefs.current[index + 1].focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otpValues[index] && otpRefs.current[index - 1]) {
            otpRefs.current[index - 1].focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
        if (!pasted) {
            return;
        }

        const nextValues = Array.from({ length: OTP_LENGTH }, (_, index) => pasted[index] || "");
        setOtpValues(nextValues);
        const nextFocusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
        otpRefs.current[nextFocusIndex]?.focus();
    };

    const handleVerifyOtp = async () => {
        const otp = otpValues.join("");

        if (otp.length !== OTP_LENGTH) {
            setShowValidation(true);
            setErrorMessage("Masukkan kode OTP lengkap.");
            return;
        }

        setLoadingAction("verify-otp");
        resetFeedback();

        try {
            const response = await verifyOtp(userId, {
                channel: selectedChannel,
                otp,
            });

            const nextUser = response?.data?.user || response?.user || response?.data || response || {};
            const mergedUser = {
                ...(userRecord || {}),
                ...nextUser,
                emailVerified: Boolean(nextUser.emailVerified ?? userRecord?.emailVerified),
                phoneVerified: Boolean(nextUser.phoneVerified ?? userRecord?.phoneVerified),
            };

            setUserRecord(mergedUser);

            let tokenToSave = response?.data?.token || response?.token;
            let tokenExpires = response?.data?.expiresIn || response?.expiresIn;

            if (!tokenToSave) {
                const session = await getAuthToken(userId);
                if (session?.token || session?.data?.token) {
                    tokenToSave = session.token || session.data.token;
                    tokenExpires = session.expiresIn || session?.data?.expiresIn;
                }
            }

            if (tokenToSave) {
                showToast("Verifikasi berhasil.");
                loginData(tokenToSave, tokenExpires);
            } else {
                setNotice("Verifikasi berhasil, tetapi gagal menyimpan token sesi.");
            }
        } catch (error) {
            setErrorMessage(mapAuthError(error));
        } finally {
            setLoadingAction("");
        }
    };

    const handleProfileUpdate = async (nextProfile) => {
        if (!userId) {
            return;
        }

        setLoadingAction("profile-update");
        resetFeedback();

        try {
            const response = await updateUserProfile(userId, nextProfile);
            const nextUser = response?.user || response?.data?.user || response || {};

            setUserRecord((current) => ({
                ...(current || {}),
                ...nextUser,
                emailVerified: nextProfile.email && nextProfile.email !== current?.email ? false : Boolean(nextUser.emailVerified ?? current?.emailVerified),
                phoneVerified: nextProfile.phone && nextProfile.phone !== current?.phone ? false : Boolean(nextUser.phoneVerified ?? current?.phoneVerified),
            }));

            if ((nextProfile.email && nextProfile.email !== userRecord?.email) || (nextProfile.phone && nextProfile.phone !== userRecord?.phone)) {
                const channelToResend = nextProfile.email && nextProfile.email !== userRecord?.email ? "email" : "phone";
                await handleSendOtp(userId, channelToResend, false);
            }

            setNotice("Profil diperbarui.");
        } catch (error) {
            setErrorMessage(mapAuthError(error));
        } finally {
            setLoadingAction("");
        }
    };

    return (
        <div className="flex flex-col gap-4 w-full">
            {(notice || errorMessage) && (
                <div className={`rounded-xl border px-4 py-3 text-xs ${errorMessage ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
                    <p className="leading-relaxed">{errorMessage || notice}</p>
                </div>
            )}

            {step === "input" && (
                <>
                    <button 
                        onClick={() => showToast("Fitur ini akan segera datang")}
                        className="flex w-full items-center justify-center gap-3 rounded-xl border border-zinc-200 px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50 cursor-pointer"
                    >
                        <svg viewBox="0 0 24 24" className="h-5 w-5" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        Continue with Google
                    </button>

                    <div className="flex items-center gap-3 text-xs text-zinc-400 uppercase tracking-widest my-2">
                        <hr className="flex-1" />
                        <span>Or Try With</span>
                        <hr className="flex-1" />
                    </div>
                </>
            )}

            {step === "input" ? (
                <div className="flex flex-col gap-4 animate-in slide-in-from-left-4 fade-in">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-zinc-600">
                            {isPhone ? "Phone Number" : "Email Address"}
                        </p>
                        <button
                            type="button"
                            onClick={handleToggleMethod}
                            className="text-xs font-semibold text-primary hover:text-primary-dark transition cursor-pointer"
                        >
                            {isPhone ? (mode === "login" ? "Login with email" : "Register with email") : (mode === "login" ? "Login with phone" : "Register with phone")}
                        </button>
                    </div>

                    {isPhone ? (
                        <div className="flex flex-col w-full">
                        <label htmlFor="auth-contact" className="sr-only">{isPhone ? "Phone Number" : "Email Address"}</label>
                        <div className="flex items-center gap-0 overflow-hidden rounded-xl border border-primary-light bg-white focus-within:border-primary transition">
                            <span className="border-r border-primary-light px-3 py-3 text-sm text-zinc-400 select-none bg-zinc-50">
                                +62
                            </span>
                            <input
                                id="auth-contact"
                                name="contact"
                                type="tel"
                                autoComplete="tel"
                                inputMode="numeric"
                                placeholder="8xx-xxxx-xxxx"
                                value={inputValue}
                                onChange={handleInputChange}
                                onPaste={handlePaste}
                                className="flex-1 bg-transparent px-3 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
                            />
                        </div>
                        </div>
                    ) : (
                        <>
                        <label htmlFor="auth-email" className="sr-only">Email Address</label>
                        <input
                            id="auth-email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            placeholder="Email Address"
                            value={inputValue}
                            onChange={handleInputChange}
                            onPaste={handlePaste}
                            className="w-full rounded-xl border border-primary-light px-4 py-3 text-sm focus:border-primary focus:outline-none transition-colors"
                        />
                        </>
                    )}

                    {mode === "register" && (
                        <div className="flex flex-col gap-2">
                            <label htmlFor="auth-name" className="text-xs font-semibold text-zinc-600">Nama Lengkap</label>
                            <input
                                id="auth-name"
                                name="name"
                                autoComplete="name"
                                type="text"
                                placeholder="Nama lengkap"
                                value={nameValue}
                                onChange={(e) => setNameValue(e.target.value)}
                                className="w-full rounded-xl border border-primary-light px-4 py-3 text-sm focus:border-primary focus:outline-none transition-colors"
                            />
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={handleContinue}
                        disabled={isBusy}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-dark mt-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        {isBusy && <Spinner />}
                        {mode === "login" ? "Continue" : "Register"}
                    </button>

                    {showValidation && !canContinue && (
                        <p className="text-[11px] text-red-500">
                            {validationMessage}
                        </p>
                    )}

                    {mode === "register" && (
                        <p className="text-[11px] text-zinc-500">
                            Dengan mendaftar, kamu menyetujui{" "}
                            <Link href="/terms-conditions" className="font-semibold text-primary hover:text-primary-dark transition">
                                Syarat &amp; Ketentuan
                            </Link>{" "}
                            dan{" "}
                            <Link href="/privacy-policy" className="font-semibold text-primary hover:text-primary-dark transition">
                                Kebijakan Privasi
                            </Link>.
                        </p>
                    )}
                </div>
            ) : step === "channel" ? (
                <div className="flex flex-col gap-4 animate-in fade-in">
                    <button
                        type="button"
                        onClick={() => setStep("input")}
                        className="flex items-center cursor-pointer gap-2 font-cormorant text-lg font-semibold text-zinc-900 transition hover:text-primary"
                    >
                        <span className="text-base">&larr;</span> Back
                    </button>
                    <div className="rounded-2xl border border-primary-light bg-zinc-50 p-4">
                        <p className="text-sm font-semibold text-zinc-900">Pilih channel verifikasi</p>
                        <p className="mt-1 text-xs text-zinc-500">Kode OTP akan dikirim ke channel yang kamu pilih.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {availableChannels.map((channelValue) => (
                            <button
                                key={channelValue}
                                type="button"
                                onClick={() => handleChannelSelect(channelValue)}
                                disabled={isBusy}
                                className="rounded-xl border border-primary-light px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:bg-primary/5 disabled:opacity-60"
                            >
                                {channelValue === "email" ? "Email" : "Phone"}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => setStep("input")}
                        className="text-left text-xs font-semibold text-primary hover:text-primary-dark transition"
                    >
                        Ubah data akun
                    </button>
                </div>
            ) : step === "otp" ? (
                <div className="flex flex-col gap-4 animate-in slide-in-from-right-4 fade-in">
                    <button
                        type="button"
                        onClick={() => setStep("input")}
                        className="flex items-center cursor-pointer gap-2 font-cormorant text-lg font-semibold text-zinc-900 transition hover:text-primary"
                    >
                        <span className="text-base">&larr;</span> Back
                    </button>
                    <p className="text-xs text-zinc-500">
                        We&apos;ve sent a {OTP_LENGTH}-digit code to {selectedChannel === "phone" ? contactLabel || "+62" : contactLabel || "your email"}.
                    </p>
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-zinc-800">Enter {OTP_LENGTH}-digit OTP</p>
                            <p className="text-[11px] text-zinc-500">
                                {otpSecondsLeft > 0 ? `Expires in ${formatCountdown(otpSecondsLeft)}` : "Kode kedaluwarsa"}
                            </p>
                        </div>
                        <div className="flex gap-2 justify-between" onPaste={handleOtpPaste}>
                            {Array.from({ length: OTP_LENGTH }, (_, index) => index + 1).map((i) => (
                                <div key={i} className="flex flex-col">
                                <label htmlFor={`otp-${i}`} className="sr-only">OTP Digit {i}</label>
                                <input
                                    id={`otp-${i}`}
                                    name={`otp-${i}`}
                                    type="text"
                                    autoComplete="one-time-code"
                                    maxLength={1}
                                    ref={(el) => {
                                        otpRefs.current[i - 1] = el;
                                    }}
                                    value={otpValues[i - 1]}
                                    onChange={(e) => handleOtpChange(i - 1, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(i - 1, e)}
                                    className="h-12 w-12 rounded-xl border border-primary-light text-center text-lg font-semibold transition-colors focus:border-primary focus:outline-none"
                                    aria-label={`OTP digit ${i}`}
                                />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 text-xs text-zinc-500">
                        <button
                            type="button"
                            onClick={() => handleSendOtp(userId, selectedChannel, true)}
                            disabled={isBusy || otpSecondsLeft > 0}
                            className="font-semibold text-primary transition hover:text-primary-dark disabled:cursor-not-allowed disabled:text-zinc-400"
                        >
                            {isBusy && loadingAction === "resend-otp" ? (
                                <span className="inline-flex items-center gap-2">
                                    <Spinner /> Sending...
                                </span>
                            ) : (
                                "Resend OTP"
                            )}
                        </button>
                    </div>

                    <button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={isBusy}
                        className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        {isBusy && loadingAction === "verify-otp" && <Spinner />}
                        Verify
                    </button>
                </div>
            ) : null}

            <p className="text-center text-xs text-zinc-500">
                {mode === "login" ? "Belum punya akun?" : "Sudah punya akun?"}{" "}
                <button
                    type="button"
                    onClick={handleToggleMode}
                    className="font-semibold text-primary hover:text-primary-dark transition cursor-pointer"
                >
                    {mode === "login" ? "Tap untuk daftar" : "Tap untuk masuk"}
                </button>
            </p>
        </div>
    );
}
